'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { decodeFrame } from '@/lib/effects/decode';
import { prefersReducedMotion, runWhenIdle } from '@/lib/utils';

const DECODE_DURATION_MS = 650;

/**
 * Site-wide micro-wow layer. Two things, both deferred to idle so nothing
 * lands on the critical path (task-28 mobile LCP budget):
 *
 *   1. Eyebrow decode-in — every `.eyebrow` label scramble-decodes like a
 *      terminal the first time it scrolls into view. Same-length mono text,
 *      so no layout shift; skipped entirely under prefers-reduced-motion.
 *      Re-scans on every route change: this component lives in the persistent
 *      (main) layout, so a []-deps effect would only ever see the first
 *      page's eyebrows.
 *   2. Console signature — a one-time styled console.log for whoever opens
 *      devtools on an engineering studio's site (they do).
 *
 * Renders nothing; it's a DOM-level enhancement, same pattern as the GSAP
 * contexts elsewhere on the site.
 */
export function AmbientWow() {
  const pathname = usePathname();

  useEffect(() => {
    let cancelled = false;
    const cleanups: Array<() => void> = [];

    runWhenIdle(() => {
      if (cancelled) return;

      printConsoleSignature();

      if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') return;

      const decoded = new WeakSet<Element>();

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || decoded.has(entry.target)) continue;
            decoded.add(entry.target);
            observer.unobserve(entry.target);
            runDecode(entry.target as HTMLElement, cleanups);
          }
        },
        { threshold: 0.6 },
      );

      // Only touch eyebrows that are a single plain text node — anything
      // with markup inside is left alone.
      document.querySelectorAll('.eyebrow').forEach((el) => {
        if (el.childNodes.length === 1 && el.firstChild?.nodeType === Node.TEXT_NODE) {
          observer.observe(el);
        }
      });

      cleanups.push(() => observer.disconnect());
    }, 3000);

    return () => {
      cancelled = true;
      cleanups.forEach((fn) => fn());
    };
  }, [pathname]);

  return null;
}

function runDecode(el: HTMLElement, cleanups: Array<() => void>) {
  const original = el.textContent ?? '';
  if (!original.trim()) return;

  let raf = 0;
  const start = performance.now();

  const tick = (now: number) => {
    const progress = (now - start) / DECODE_DURATION_MS;
    el.textContent = decodeFrame(original, progress);
    if (progress < 1) {
      raf = requestAnimationFrame(tick);
    }
  };

  raf = requestAnimationFrame(tick);
  cleanups.push(() => {
    cancelAnimationFrame(raf);
    el.textContent = original;
  });
}

let signaturePrinted = false;

function printConsoleSignature() {
  if (signaturePrinted) return;
  signaturePrinted = true;
  try {
    console.log(
      '%c datacode %c web · ai · mobile — built in Kurdistan, shipped worldwide\n' +
        '%c ↳ you read consoles. we hire people like you: https://datacode.app/apply',
      'background:#173835;color:#34d399;font-weight:bold;padding:4px 8px;border-radius:3px;',
      'color:#9ca3af;padding:4px 0;',
      'color:#6b7280;padding:2px 0;',
    );
  } catch {
    /* consoles that reject %c styling are not worth crashing for */
  }
}
